import React, {useState, useEffect} from "react";
import {useSearchParams} from "react-router-dom";
import { useVsiAppContext} from "../../../../stores/VsiApp";
import {Card, Spinner} from "reactstrap";
import {getReport} from "../../../../utils/Verbs";
import {VsiBubble, VsiH4} from "../../../../styled/App.styled";
import Grid from "../../../../components/Grid";
import {useTitleFromComponent} from "../../../../utils/hooks";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import themeStyles from "../../../../utils/themeStyles";
import "./HostConnectedSwitches.css";

require("highcharts/modules/networkgraph")(Highcharts);
require("highcharts/modules/accessibility")(Highcharts);
require("highcharts/modules/exporting")(Highcharts);


const connectedSwitchesDefs = [
    {
        "field": "host_name",
        "headerName": "Host Name",
        "enableRowGroup": true
    },
    {
        "field": "switch_name",
        "headerName": "Host Switch Name",
        "enableRowGroup": true
    },
    {
        "field": "host_wwn",
        "headerName": "Host WWN"
    },
    {
        "field": "host_port_name",
        "headerName": "Host Port Name",
        "enableRowGroup": true
    },
    {
        "field": "host_port_internal_name",
        "headerName": "Host Port Internal Name"
    },
    {
        "field": "host_port_alias",
        "headerName": "Host Port Alias"
    },
    {
        "field": "host_port_errors",
        "type": "numericColumn",
        "headerName": "Host Port Errors"
    },
    {
        "field": "storage_name",
        "headerName": "Storage Name",
        "enableRowGroup": true
    },
    {
        "field": "switch_name",
        "headerName": "Storage Switch",
        "enableRowGroup": true
    },
    {
        "field": "storage_type",
        "headerName": "Storage Type",
        "enableRowGroup": true
    },
    {
        "field": "storage_port_name",
        "headerName": "Storage Port Name",
        "enableRowGroup": true
    },
    {
        "field": "storage_port_internal_name",
        "headerName": "Storage Port Internal Name",
        "enableRowGroup": true
    },
    {
        "field": "storage_port_alias",
        "headerName": "Storage Port Alias",
        "enableRowGroup": true
    },
    {
        "field": "storage_port_errors",
        "type": "numericColumn",
        "headerName": "Storage Port Errors"
    },
    {
        "field": "storage_wwn",
        "headerName": "Storage WWN",
        "enableRowGroup": true
    }
];


export default function HostConnectedSwitches({title}) {
    const { theme, client, periodId, user, getAccessTokenSilently } = useVsiAppContext();

    const [searchParams] = useSearchParams();
    const [connectedSwitchesData, setConnectedSwitchesData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const loadFunction = function () {
        const rowSpace = 60;
        const colSpace = 40;
        const portRowLimit = 8;
        const chart = this;
        // Pick particular node from nodes array:
        const HostNode = chart?.series?.[0]?.nodes?.filter(s => s.category === 'HOST')?.[0] ?? {};
        const StorageNodes = chart?.series?.[0]?.nodes?.filter(s => s.category === 'STORAGE');
        const PortNodes = chart?.series?.[0]?.nodes?.filter(s => s.category === 'PORT');

        let SwitchNodes = [];
        PortNodes?.forEach(p => {
            if (SwitchNodes.indexOf(p.switch_name) < 0) {
                SwitchNodes.push(p.switch_name)
            }
        })

        HostNode.fixedPosition = {
            x: chart.plotWidth / 2,
            y: 450
        };
        HostNode.plotX = chart.plotWidth / 2;
        HostNode.plotY = 450;

        StorageNodes?.forEach((node, idx) => {
            node.fixedPosition = {
                x: (chart.plotWidth / 2),
                y: 0
            };
            node.plotX = (chart.plotWidth / 2);
            node.plotY = 0;

        })
        this.switches?.forEach(s => s.destroy());
        this.switches = [];
        let totalWidth = 0;
        let SwitchNodes2 = SwitchNodes?.map((s, idx) => {
            const PortSwitchNodes = PortNodes.filter(f => f.switch_name === s);
            const sWidth = PortSwitchNodes.length >= 8 ? 480 : PortSwitchNodes.length > 1 ? (480 * PortSwitchNodes.length) / 8 : 80;
            totalWidth += sWidth;
            return {
                label: s,
                width: sWidth,
                height: rowSpace + rowSpace * Math.floor(PortSwitchNodes.length / portRowLimit)
            };
        });
        let xPos = (chart.plotWidth / 2) - (totalWidth / 2);
        
        SwitchNodes2?.forEach((s, idx) => {
            const PortSwitchNodes = PortNodes.filter(f => f.switch_name === s.label);
            this.switches.push(this.renderer.label(s.label, xPos, 200)
                .attr({
                    fill: 'yellow',
                    zIndex: -10
                })
                .add()
            );
            PortSwitchNodes?.forEach((port, pidx) => {
                const nodeXPos = ((pidx % portRowLimit) * rowSpace) + xPos + 20;
                const nodeYPos = 160 + colSpace * Math.floor(pidx / portRowLimit);
                port.fixedPosition = {
                    x: nodeXPos,
                    y: nodeYPos
                };
                port.plotX = nodeXPos;
                port.plotY = nodeYPos;
            });

            this.switches.push(this.renderer.rect(xPos, 200, s.width, s.height, 5, 2)
                .attr({
                    stroke: 'yellow',
                    zIndex: -10
                })
                .add()
            );

            xPos += (20 + s.width);

        });
    };
    const [chartOptions, setChartOptions] = useState({
        annotations:
            [{
                shapes: [{
                    type: 'path',
                    fill: 'rgba(255,0,0,0.75)',
                    points: [{
                        xAxis: 50,
                        yAxis: 200
                    }, {
                        xAxis: 100,
                        yAxis: 200
                    }],
                }]
            }],
        chart: {
            type: "networkgraph",
            marginTop: 80,
            backgroundColor: themeStyles[theme].cardBgColor,
            height: 600,
            width: 1700,
            events: {
                load: loadFunction,
                redraw: loadFunction
            },
            scrollablePlotArea: {
                minWidth: 700,
                scrollPositionX: 1
            }
        },
        credits: {
            enabled: false,
        },
        title: {
            text: "Host Switch Connections",
            style: {
                color: themeStyles[theme].textColor
            }
        },
        subtitle: {
            text: searchParams.get("host_name"),
            style: {
                color: themeStyles[theme].textColor
            }
        },
        tooltip: {
            enabled: true,
            style: {
                color: "#000000"
            },
            formatter: function () {
                return "<b>" + this.point.category + ": " + this.point.name + "</b>"
            }
        },
        verticalAlign: "top",
        plotOptions: {
            networkgraph: {
                keys: ["from", "to"],
                lineWidth: 2,
                layoutAlgorithm: {
                    enableSimulation: false,
                    theta: 0.5
                }
            }
        },
        series: []
    });

    const encodedDevice = encodeURIComponent(searchParams.get('device_name'));
    const encodedHost = encodeURIComponent(searchParams.get('host_name'));

    useTitleFromComponent(`${searchParams.get("host_name")} / ${title}`);
    
    const getConnectedSwitches = async function () {
        try {
            const token = await getAccessTokenSilently();
            const route = `/api/v6/report/host_connected_switches_proc?client_id=${client.clientId}&period_id=${periodId}&host_name=${encodedHost}&device_name=${encodedDevice}`;
            const initData = await getReport(route, null, token);
            setConnectedSwitchesData(initData?.data?.recordset || []);
            loadChart(initData?.data?.recordset || []);

        } catch (err) {
            console.error(err);
            setIsLoading(false);
        }
    };

    const connectionExists = ((lines, con2) => {
        let flag = false;
        lines.forEach(con1 => {
            if (
                con1["to"] === con2[1]) {
                flag = true;
            }
        });
        return flag;
    });

    const nodeExists = (nodes, node) => {
        let flag = false;
        nodes.forEach(n => {
            if (n["id"] === node) {
                flag = true;
            }
        });
        return flag;
    };

    const portConnExists = (port, port_list) => {
        let flag = false;
        port_list.forEach(p => {
            if (p[1] === port["storage_port_name"] && p[2] === port["switch_name"]) flag = true;
        })
        return flag;
    }

    const loadChart = (data) => {
        let tmpChartData = [];
        let colors = Highcharts.getOptions().colors;
        let color_idx = 0;
        let tmpChartNodes = [];
        let hostPortConections = [];
        let devicePortConnections = [];
        data.forEach(d => {
            const conHP = [d["host_name"], d["host_port_name"], d["switch_name"]];
            hostPortConections.push(conHP);
        });
        data.forEach(d => {
            if (!portConnExists(d, hostPortConections)) {
                const conDP = [d["switch_name"], d["storage_port_name"], d["device_link_to_switch"]];
                devicePortConnections.push(conDP);
            }
        });
        hostPortConections.forEach(d => {
            if (!connectionExists(tmpChartData, d)) {
                if (!nodeExists(tmpChartNodes, d[0])) {
                    tmpChartNodes.push({
                        id: d[0],
                        category: 'HOST',
                        color: colors[color_idx],
                        marker: {
                            radius: 60,
                            symbol: "url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAA8CAYAAAAkNenBAAAAAXNSR0IArs4c6QAAEnFJREFUaEPFmgtcVNW+x3973jDADAgiAgMiRoii4DPT8oXm46hlPurTqeycW9mnk6Uezbdmp7J7LTPRLCw7aUadoxaHwmMZqMiAibdrBokPQFDewsC8995zP/81bBp0kMH63Ls+n/kMzF57rfVd/+f6783h929amUy2PjExcUn//vGq0tISW2Vl5SWbw/FviOIhAGcBmH/vabnfaUAFgGcMBsPrf12xInBYaio47tahnU4nyisqkJebi7y8PEt5eXkhz/MfA/gXgMbfspbfCpKm0+k+eP7552OmT58OmUzW47WIogvXrlUjOzsb33zzTUN9ff0eURT/C0BDTwa7E5A4hUL54cPzHr7/maefhkaj6cl83fZ1uVwoKyvDokWL3hBFcVW3N7R38BXETyaTvTo0JeWFNatXK/r27evr+D3uRxsTGBiIhISEbwVBSPN1gO5AZoeFhb2/cuXLvceMucer3vs6UXf9tFotQkJCYLfbQVJJSko6a7fbU7u7T7ruDSRcLlf8ff78eVOeeeZZqNUqX8e6o37+/lqEh4fDbG7ruF8URaSkpJS2tbUl+jqoB4h8fkxMVMbmzZsDBwwY0Ol+2iHJC3n7u6vr3hZBfan5+fkhIqIvbDYrBEHo1JVAhg0bVm4ymfr1BCRh0qRJJevWrePUanW393kuWurcExClUgmyMVosqZG3RtdGjRpV3dTUFNXtgjyMfUVBQcEWlUoFuVzOBqeBump3CkKumSSgUMhhtVpvuz6af8yYMbX19fV9fAaRyWTv5ufnP+95A0EFBQUxdWpra4PNZmMGSK2nqkVjkA1oNH6wWHwL6AQybty4hpqamrCegGTm5+fPv90NpM86nY5JzGKxMDiK0t2pVlhYGMgbkQSkjfBlYQRy3/33N12/dq2XL/2pD6dQKL4+ceLENJJAQ0MDSIdvTi88pUDXaHHUX1LF1tbWTlILDQ1lfTwl6euCqB8Z/4QJE5urq6uCfb2PDPxEbm7u2N69e6Ouro7ZB+067bi/vz+6cwAEQ9KihVOj/wngdnbW3eIIZNKkSS1Xr17Vd9dXuk4gZ3Nzc4dKIDffeDMY2Y+3hJDuI5jIyCg0N9/wdX6v/Qhk8uTJpsrKSp2vA3HagIDSb48eTegKpCdgJMGoqOjfCSTNVFlZ4TuITqe7nJOT089XkJvBmpqaWGpBjZxCdDSBNPu6kV1LJC2ttbKiIsjXgbiQkF5V2dn/iiSQ2tpa8DzP7lUoFD7lVp4glPBFRxvQ0vLbQGgNU6ZMaa3oCUhY7961X335Ze+bJUKeitwmGT6pDO22N9vwBCHHYDDE/P+ARERENB08eDCYghZJpKtGu9TY2AibzY6QkGAEBAQwME8QcgQxMbE+gwiCCJ4X2DgU8WUyd+pHc6VNmdIz1TIYDC2ZmZlBPbERkhYFxdraOvj5aRAZGckWQDGIQEymFq/7UV5eh8zMkygpqWKL1wcHICDQH3RKbGluQ6vJDKVSgZEj4/HZZ2+ZKiou+m7scXFxbfv379d6xpGeHFk9JeINhKBp8UeOnMWf/jwdaWnDEKALgkohg1IuAx3t7byIkspmaNRKyGXA5YtV+CQjC1cuXrWJIvdYYeGOf3Zn9FxCQoJl7969fp6qRZNTUHM4HMw+aIFdNU8QchAkkdZWE+t++nQZtm/PxoqXF2LypBRcrLNDyYlINNwasPflFEMXGoHaZiuaTDYMitEhQq/B/g+zcOr7M2aZDEmnTqVXdLUOLikpyZqRkaG5nY0QEC2YWnBwcKdof+PGDfab5OkkkE2bPsO99ybjyUXTQDUJi8OFH680IVgXCI1cRP9w/441FZ05C4tfDBxOHnanALuDZ0CtFjseHB2F+hsWbP7ru2hrNW8oKEh/xRsMl5w8xL5793uq7oxdupmk1dLSgvqGBvhpNAyKkkMJhLzWY4+9ia1bFyNpUD/U1zfgTIUFd0eHIFDhQJ1Ng4QIP2bYB7NyoA6ORkSUAXoNh4oGO+xOHjYHj14BKowbGIpTRWcRZ4jEL1cb8N8FP+PzzONZRmP6rJthuNTUVEd6erqSQGpqatj1rlIQbzvhqVqUZ61d+wX27l2JltYbKLrQgKiYWAyPD8OFyhqEhPUB+SWNAjiSfRijxs+EUxDg4AVcv3YNgfowBtJHp0JyjA4Hs7/DyOGp0GmVMLWaEeCvwd69OcjMzD9oNO6Y67kebsTIkc7t77yj8JQI5ToUQwiI4sftjN9Ttd7eloM1ax7H4EH9kFfSCD+NGtEhavTVq+DkedSY5WxukqogusDzIpy8ADvvhrE7BISpzKhokcPUVI/6ihIsnDcH/goXi2dWq4WtZ/PmfSgsLFtiNKZvl2C4MWPGCFu3bpV1pVo0qdlsZupESSGVamjnpSaBnDlzBTa7AsuWzus4e+QXl2LsMHf9gMYpr66HItCthvQ/L7jgcLZDOHlEBMqg1ypR02yDIdQPDh7QahRsExiIxcK+6QjxyKOvw2I29Soo2MOMlxt3333im1u2cL7aCKX3dG5pM5sREhwMjpMhOFiPVas+R27u2x0QknpS9vzF4Wzoe0di6OCBsLt+rQuILjcIM3AydIcA0VTdAS9tljsDpyzDzEDoOC6TK/DE49tqjMb0CAYyYcIE8bXXXmMgPbUR2lWSyHfflWHRU7OQNDC2Q1ISiCCKuHDhIhTBBsjkcpbDdTQCaVcpm1NgajYgXIOQAKX7TCOI0Gr92eZYrbYOEIKhvO71N/bjp3PVqQUF753l0tLSxFdeeaWTRKQzCE1IcaQ7G3nzzaM4dmxrJ19AIM3NLfh3fjFGjRnf2U+0F6FogZJEbE4ew2K0EAUBOw4cRVR8MoYblAgPC0Fj4w2E6AI6gRCMVhuAJ5/cXm00pkdxDzwwTdywYX2XqkWT0U2Umnue3aWVFRWVItoQjzmz773JqXHI+7Ec/WJjAHfdgn15VgRFuOBwuGMHSQTmBhQWGjFo9GS0WZ2ovl4HP20AbrTacbfOhJTEONjtJBkLs1s6mT7z7DbUXK9TczNmzBDXrl3rs41QQkc20traBn2wHh99ZMTnmZsgp9yivRH8rn1f4r5JDyDAX80Wz3ki0A8uuF1vOwTB9AtVITzo18rm6bIG2HkXrta3obCkFktn9gMEOwOhD3nXwtPnsf+TM49zs2bNEletWsVAyJCJtKvC2c1xhBa8evUX+P77t5gel1y4hB8vNyKlfy80IgxqlQL+agX8NbemOOR+nWTkZCNOAb20csSFea/su7XCitq6OshlHIMo/PEC4vqGQKcLwuLFGWe4OXPmiCtXrmQSJwOi9FxyrwREWa502PIGsm9fEXakL8GGdz9HwtAx8FMrIHc5ERQYALVKzpJDrUYFlVLOZELqRTbIYoggMmN38CLu6R/oLd6y33iKaxYrNuz+Gs89OBx2mxV5567j+E/1eH7mXVixdK+Ne2juXPGvy5ffUswmAycogiPDlQ5aJDGpQmK3O3H5ihlPPjEVb/+zGAn9ItFq41kWq1bKoVbIGIBCLmOSoW8aR3TR4twgBBShUyK2C2kQiN3hYDGE5t7z7WU8mKqH2exWr+JqDod2H3RxDz88z7Vs2dJO+u0tRaGcigKilAmTftbUNCDzH8V4561nwYsuKGQcPsmrhEatYiAsVVfIGACdmeibpEQg5JZ5noKiiMS+/gjXea/6U6yhLINA6PvVj4/j8bS7wQl27N6XhbuGT0bOR1ng5s9f4HrppRc7QEwmEw4cOAC9Xo/k5CGIj+9/SxovFekcDhf2fHwc98+bCZelHhNS41BeWY3SVh3USgXUSgLgmPumDJgM3gUX+yZAd3QXMTAqAIZe3u2DMm+KIZSeEAh5smaLgP/88Gu4gmIxdUQMcvZmg1uwYIHrxRd/BaEqobGwEPknT7KgJDXKcFNSUmAwGDxsyIn3PjiJBX95DD9fNTEpzB8dgdIaGyYO7s2kYHEI+K6wBE51KPNdBOIS23MtUi9eYNJ5amI/dsjybO6cz8YePTCptH9IpX4pu4wPT7Zixj398VVGlotbuHCha8mSJZ0GoOhLJZ4jR46gqKjolsI17fDTTz/Nfl+77iDWbVuG78/VMimQOqk4Hn+ZNahjzNY2M05caMMNsxNUCyd1YQZPNtJu8HTfnybGoqqmHmcvXMOIxGj0Cg6EzQOENtadPNqwYWcWeK0Bs8cl4OD7X1m5Rx55xPXCCy/c4jEIhg5MpEbuBzEc2zEqQJSWltIzPnbPsmWfYvvH6/FFfgVUBKKU4w/DI5Ece2u1M+NoGUTI3RKhDFhwq5ajvQhhtdrBuzgWW5Ry4OU5A1gAJACShvR97ucL2F9ohYu344GR/ZH9yVFjlyASmVu/ZQzGW0X9nXdy8B/LFyH7bC2rhJDub3p0CLMNb62ywYLDhVXM5TIQyRXz7lyLZcNOmkvE+rn9WUwjAE+YlzOMUKoDEKqVI0wm4qei0gXdgnTp3NsvXL5Shx/+pxGx94zCpesmFgT/9sehzI5u96R11fvHoA4MZRKhshC5YgmEnVGcPDY+FAve6egEc6O5BVuzqiA6rJg4rB+OHzoBGadXerWR7hbveZ2ktHz5Afxt1yocyLvEXOwfx8ciOTYYcgXFEDlTT0+XTp5o9cc/QK5Uuw9Y7RDSQcvB80wqc1KDkRShbAexs2/K+bZ/XQ2Z4MCk4XHI+Sz3itGYHneL1+oJhNT3q6+KEWyIQ6s2FNWNFigVcryy4C4oFUomGRmLI+7SD9na+u0H4NAnMqP/Vb2EDiAW7Z0CXkoLg79CYAB2u6NdvSzYnlWB0UlRKCk4j+YGU2JBwY5Sbt68+a6lS1+6k/V3CqIklTXblmP/sTKWQJKtbJxLMUjBbMxTKjs/PYIqxDDPJUmEXDCplLP9+Etp0coHQsGJTlaWYlmv1Yp39x1DQGg8hidG4tihUxVGYzo7BHFz584Vl3ukKOSVpOMkHWt9LdadP1+Fo3mXMf3xOcgqLGcwo/sH4Q+pvTqBkCq+tDMX2jADqzDSh0X59nSF8iqCoFLqwuFaGPRg0nA47CguqUZ+qQVT7k3Ekcw8KBV8YG7uTvaAvlPSKG0xiZJOi5KXonyLcn8p7+pKfLt2fQfDoIHQxsTi1M81GJ8QiLRBug5p1Dc0YkeeGZDJ23Mud2AkY3cnkgLzjqLAw8U7EagS8NS9ge0SsSP90xOYOnUKivN+xI365icKCnb+XVoLN3PmTHHNmjWdHAwNSpIgICr3eKb1pCKUTBJYp2Nr+4ivvvolRk8fD+h7oai0Bqunh3aAnCspw8GL7vjCkkcmETeEKBKACJfgdH/o8YbgwLMTdAzkek09zIpIlBZfwrXy2j1G484/e24oN3XqVHHjxo2dQCj3r6urZRKgnEt6jkgT0gKk2OJNMlRDXrIkA3eNSMawMUkI5+o7guqr+4xQhg9k0Z3GcX9EQBTgojgl8nAJBEAwJBUHZg/mkPvDFYwYNRpnTpSgsaZ5r9GYvujmubn7x48X33j99VtcPk1CjxmkfEtK66kU400S0sAEQm3r1sNoaGzD+nVzmVRXfHAKqmCD+0TiJnFDuES46AUFkQB4uBiUk/0tE3gMuasvggNUyM0qAu/knzMa03d520Bu1KhR/LZt234tVLX3IgBaMH1Ib6X3Rej/2zkACYSGKS2twmuv/QMj7olH/OhRKLvahHOXaliN1w1DABIMAfBMOgEqGZLi+0CvVaP45HnUX2to4HncXVSU3uVbdtygwYNtH7z/vteXUOj5OXkxWjzZhPRw53a+2hNE6peTU4xPPz2OsMgwDB07BLqQINgdTrS0UUbrYEAalZwdvjiXixaO8z9cgKXV2szzrtmnT6cf7y4+cOHhfa4dPnyIFbm6apL38qUm7A2ExiUJ79mzD8eP1x5WKv0nchyCFEolFCoFk47T4QTvFOgkXCOKrg8A2dbCwnfdzyd8aGQb6oEDB5oyMjJUviy0uzG9gZBkN27chMOHDw0G8JPHGNz48RuZWufmbqQUu71w1N0st16XjJxTKJRH09PTJyUn01x33m4GIUN/5NFHrRfLyqjo69tbNXcw/c3eKlqv1+dv2bIlOjk5+Q6GAyQQUseqqipMnz79Z4vFQqesO95tXxbSVaYdIpfL90ybNm324sWLOemFAF8GJBCKN/mnTuGpRYt2iaL4nC/3/dY+3b2cSeMPkMlkmwYMGPDgQw89pBk7dmzHydHb5BRAt23bht27d88A8PVvXaCv9/sC4jkW9aeoNpXj5DOCggKG0JsGfSIiNL3DwujZCVdQUGD55ZdfogG4Hzr+H7X/BTjvPlkObrVXAAAAAElFTkSuQmCC)",
                            height: 60
                        },
                        zone: d[3],
                        port: d[1]
                    });
                }
                if (d[1] && d[1] !== '') {
                    tmpChartData.push({from: d[0], to: `${d[2]}_${d[1]}`, zone: d[3]});
                    if (!nodeExists(tmpChartNodes, d[1])) {
                        tmpChartNodes.push({
                            id: `${d[2]}_${d[1]}`,
                            name: d[1],
                            category: 'PORT',
                            switch_name: d[2],
                            color: colors[color_idx],
                            marker: {
                                radius: 25,
                                symbol: "url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAEfSURBVDhPrZVBjsIwDADbXdgP9c6ZD/QFe+Y1nPcF/cCeufdBgIDNWHbkuIkqVh0J4tjp1C4g+p/z+dVtiQnHcfyURCLFXxoKwzDsNSxi8GfFxdsWMhy4ZOTv06knad2+y+/lspum6ZE7JBllPm+d1c5aZ8SsH5psjgnzPN81XIxJZ7oVRJiSN9klkJkgisHORlm+kW/XC6IsdpYWee6wGNnwncUxK52JxN8IRBg7W5E9pZDwNUOE/+3My6izVkeGhkyIMrD6YmSoyFpj9tQ1FlZ/KVYDZMfD4arbDGdyzUtIaiid1WqWo84KRc0diN8zgbp/pVQxpr9O6ry1ZOBr0JJBFureDhfPTENodgZ8sIXQH4Z4wZqMFdfGfwFd9wehZBJ8EXNdiAAAAABJRU5ErkJggg==)"
                            },
                            zone: d[3],
                            port: d[1]
                        });
                    }
                }
            }
        });
        color_idx++;
        devicePortConnections.forEach(d => {
            if (!connectionExists(tmpChartData, d)) {
                if (d[1] && d[1] !== '') {
                    tmpChartData.push({from: d[2], to: `${d[0]}_${d[1]}`, zone: d[3]});
                    if (!nodeExists(tmpChartNodes, d[1])) {
                        tmpChartNodes.push({
                            id: `${d[0]}_${d[1]}`,
                            name: d[1],
                            category: 'PORT',
                            color: colors[color_idx],
                            switch_name: d[0],
                            marker: {
                                radius: 25,
                                symbol: "url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAEfSURBVDhPrZVBjsIwDADbXdgP9c6ZD/QFe+Y1nPcF/cCeufdBgIDNWHbkuIkqVh0J4tjp1C4g+p/z+dVtiQnHcfyURCLFXxoKwzDsNSxi8GfFxdsWMhy4ZOTv06knad2+y+/lspum6ZE7JBllPm+d1c5aZ8SsH5psjgnzPN81XIxJZ7oVRJiSN9klkJkgisHORlm+kW/XC6IsdpYWee6wGNnwncUxK52JxN8IRBg7W5E9pZDwNUOE/+3My6izVkeGhkyIMrD6YmSoyFpj9tQ1FlZ/KVYDZMfD4arbDGdyzUtIaiid1WqWo84KRc0diN8zgbp/pVQxpr9O6ry1ZOBr0JJBFureDhfPTENodgZ8sIXQH4Z4wZqMFdfGfwFd9wehZBJ8EXNdiAAAAABJRU5ErkJggg==)"
                            },
                            zone: d[3],
                            port: d[1]
                        });
                    }
                }
                if (!nodeExists(tmpChartNodes, d[2])) {
                    tmpChartNodes.push({
                        id: d[2],
                        category: 'STORAGE',
                        marker: {
                            symbol: "url(data:image/png;base64,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)",
                            radius: 300
                        },
                        zone: d[3],
                        port: d[1]
                    });
                }
            }
        });
        setChartOptions({
            ...chartOptions,
            series: [{
                id: "HostConnectedSwitches",
                draggable: false,
                dataLabels: {
                    color: "#ffffff",
                    enabled: true,
                    linkFormat: "",
                    allowOverlap: false
                },
                nodes: tmpChartNodes,
                data: tmpChartData
            }]
        })
        setIsLoading(false);
    };

    useEffect(() => {
        getConnectedSwitches();
    }, [searchParams]);
    
    const filterChanged = (eventData) => {
        setIsLoading(true);
        let rowData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            rowData.push(node.data);
        });
        loadChart(rowData);
    };
    return <Card className={`report-card p-2 nav-card-${theme}`}>
        <VsiH4>Host Connected Switches - {encodedHost}</VsiH4>
        <VsiBubble noAutobot>
            {isLoading ? <Spinner color={"primary"}/> :
                <div className="highcharts-figure">
                    <HighchartsReact highcharts={Highcharts} options={chartOptions}/>
                </div>
            }
        </VsiBubble>
        <VsiBubble>
            <Grid data={connectedSwitchesData} columnDefs={connectedSwitchesDefs} filterChanged={filterChanged}/>
        </VsiBubble>
    </Card>

}
